import { CircularProgress, IconButton, InputAdornment, TextField } from "@material-ui/core";
import { ArrowForwardRounded } from "@material-ui/icons";
import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";

import { useLocalStorageState } from "../../../hooks/useLocalStorageState";
import {
    BASE_SERVER_URL,
    LS_KEY_DOMAIN_HINT,
    LS_KEY_DOMAIN_HINT_ACCESS_EMAIL,
    LS_KEY_DOMAIN_HINT_ACCESS_HINT,
} from "../../../constants";
import { useAuth } from "../context";

const DOMAIN_HINT_ROUTE = `${BASE_SERVER_URL}/api/auth/domain-hint`;

const ERROR_FAILED_REQUEST = "Failed to proceed";
const ERROR_INVALID_EMAIL = "Please enter a valid email address";

export function DomainHintAccess() {
    const { msalLogin } = useAuth();

    const [email, setEmail] = useState("");
    const [verified, setVerified] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const [savedEmail, setSavedEmail] = useLocalStorageState({ lsKey: LS_KEY_DOMAIN_HINT_ACCESS_EMAIL, initialValue: "" });
    const [savedDomainHint, setSavedDomainHint] = useLocalStorageState({ lsKey: LS_KEY_DOMAIN_HINT_ACCESS_HINT, initialValue: "" });
    const usedDomainHint = localStorage.getItem(LS_KEY_DOMAIN_HINT);

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value);
        setVerified(false);
    }, []);

    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();

        if (!verified || !email) return;

        if (savedDomainHint !== null && email === savedEmail) {
            msalLogin(savedEmail, savedDomainHint);
            return;
        }

        try {
            setError("");
            setLoading(true);
            const response = await axios.post(DOMAIN_HINT_ROUTE, { email });
            const domainHint = response.data ? response.data.domainHint : "";
            setSavedDomainHint(domainHint);
            setSavedEmail(email);
            msalLogin(email, domainHint);
        } catch (err) {
            setError(ERROR_FAILED_REQUEST);
        } finally {
            setLoading(false);
        }
    }, [email, verified, msalLogin, setSavedDomainHint, setSavedEmail, savedDomainHint, savedEmail]);

    useEffect(() => {
        if (email === "") {
            setError("");
            setVerified(false);
            return;
        }

        const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
        setVerified(isValidEmail);

        const timeout = setTimeout(() => {
            setError(!isValidEmail ? ERROR_INVALID_EMAIL : "");
        }, 500);

        return () => clearTimeout(timeout);
    }, [email]);

    useEffect(() => {
        if (savedEmail && savedDomainHint === usedDomainHint) {
            setEmail(savedEmail);
        }
    }, [usedDomainHint, savedDomainHint, savedEmail]);

    if (loading) {
        return (
            <div>
                <CircularProgress />
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit}>
            <TextField
                label="Email"
                type="email"
                variant="outlined"
                value={email}
                onChange={handleChange}
                error={Boolean(error)}
                helperText={error}
                autoFocus={true}
                style={{ minWidth: "300px" }}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                type="submit"
                                color="primary"
                                disabled={!verified || loading}
                            >
                                <ArrowForwardRounded />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />
        </form>
    );
}
